import { FormInstance, message, Modal } from 'antd';
import { contentService } from '../../service/contentService';
import { ContentStreamManifestInterface, SubtitleInterface } from '../../type';
import {
  VideoManifestUpdateResponseInterface,
  VideoManifestUpdateDetailInterface,
  UpdatedManifestInterface,
  CreatedManifestInterface,
} from '../../type/contentType';
import React from 'react';
import { UploadVideoInterface } from '../../../../shared/type/uploadVideoType';
import { originVideoUploadService } from '../service/originVideoUploadService';

interface TranscodeHandlersProps {
  id: number;
  setContent: React.Dispatch<React.SetStateAction<ContentStreamManifestInterface[]>>;
}

interface TranscodeHandlers {
  handleRefresh: () => Promise<void>;
  handleStartTranscode: () => Promise<void>;
  handleRetranscodeWithEmbeddedSubtitle: () => Promise<void>;
  handleUpdateVideoManifest: () => Promise<void>;
  handleSaveButtonClick: (
    contentId: number,
    form: FormInstance,
    uploadVideo?: UploadVideoInterface,
    onProgress?: (videoFile: UploadVideoInterface) => void
  ) => Promise<void>;
}

const transcodeHandlers = ({ id, setContent }: TranscodeHandlersProps): TranscodeHandlers => {
  const handleRefresh = async (): Promise<void> => {
    const content = await contentService.fetchById(id);
    setContent(content.content_stream_manifests || []);
  };

  const handleStartTranscode = async (): Promise<void> => {
    await contentService.videoTranscode(id);
    message.success('Transcode started');
  };

  const handleRetranscodeWithEmbeddedSubtitle = async (): Promise<void> => {
    Modal.confirm({
      title: 'Retranscode with embedded subtitle',
      content: 'Are you sure you want to retranscode with embedded subtitle?',
      onOk: async () => {
        try {
          await contentService.videoTranscodeWithEmbeddedSubtitle(id);
          message.success('Retranscode with embedded subtitle started');
        } catch (error: unknown) {
          console.log(error);
          let errorMessage = 'Unknown error';
          if (
            error &&
            typeof error === 'object' &&
            'response' in error &&
            error.response &&
            typeof error.response === 'object' &&
            'data' in error.response
          ) {
            errorMessage = String(error.response.data);
          }

          Modal.error({
            title: 'Error',
            content: `Retranscode with embedded subtitle failed, ${errorMessage}`,
            okText: 'Confirm',
          });
        }
      },
    });
  };

  const handleUpdateVideoManifest = async (): Promise<void> => {
    // 결과 모달을 표시하는 공통 함수
    const showResultModal = (result: VideoManifestUpdateResponseInterface) => {
      Modal.info({
        title: 'Video URL Refresh Results',
        width: 800,
        maskClosable: false,
        closable: true,
        content: (
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f6f6f6',
                borderRadius: '6px',
              }}
            >
              <strong>전체 결과: </strong>
              {result.message}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>성공: {result.success}개</span>
              {' | '}
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>실패: {result.failed}개</span>
            </div>

            {result.details.map((detail: VideoManifestUpdateDetailInterface, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: detail.success ? '#f6ffed' : '#fff2f0',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong>콘텐츠:</strong> {detail.content_title} (ID: {detail.content_id})
                </div>
                <div
                  style={{
                    marginBottom: '8px',
                    color: detail.success ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold',
                  }}
                >
                  상태: {detail.success ? '✅ 성공' : '❌ 실패'}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>메시지:</strong> {detail.message}
                </div>

                {detail.updated_manifests && detail.updated_manifests.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong>업데이트된 매니페스트:</strong>
                    {detail.updated_manifests.map(
                      (manifest: UpdatedManifestInterface, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#fafafa',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#666',
                          }}
                        >
                          <div>
                            <strong>해상도:</strong> {manifest.preset}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>이전 URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#666' }}>
                              {manifest.old_url}
                            </div>
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>새 URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#1890ff' }}>
                              {manifest.new_url}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

                {detail.created_manifests && detail.created_manifests.length > 0 && (
                  <div>
                    <strong>생성된 매니페스트:</strong>
                    {detail.created_manifests.map(
                      (manifest: CreatedManifestInterface, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#666',
                          }}
                        >
                          <div>
                            <strong>해상도:</strong> {manifest.preset}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>새 URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#1890ff' }}>
                              {manifest.new_url}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ),
        onOk: () => {
          const refreshData = async () => {
            const content = await contentService.fetchById(id);
            setContent(content.content_stream_manifests || []);
          };
          refreshData();
        },
      });
    };

    Modal.confirm({
      title: 'Refresh HLS Info from ODK',
      content: 'ODK로부터 HLS 정보를 다시 가져옵니다. 실행하시겠습니까?',
      onOk: async () => {
        try {
          const result = await contentService.updateVideoManifest(id);
          showResultModal(result);
        } catch (error: unknown) {
          console.log(error);

          // 400 오류일 때는 응답 데이터를 파싱해서 동일한 모달 사용
          if (
            error &&
            typeof error === 'object' &&
            'response' in error &&
            error.response &&
            typeof error.response === 'object' &&
            'status' in error.response &&
            error.response.status === 400 &&
            'data' in error.response
          ) {
            // 400 오류는 백엔드에서 정상적인 응답 구조로 오므로 동일한 모달 사용
            showResultModal(error.response.data as VideoManifestUpdateResponseInterface);
          } else {
            // 기타 오류는 기존 방식대로 처리
            let errorMessage = 'Unknown error';
            if (
              error &&
              typeof error === 'object' &&
              'response' in error &&
              error.response &&
              typeof error.response === 'object' &&
              'data' in error.response
            ) {
              errorMessage = String(error.response.data);
            }
            Modal.error({
              title: 'Error',
              content: `Failed to refresh video URL: ${errorMessage}`,
              okText: 'Confirm',
            });
          }
        }
      },
    });
  };

  const handleSaveButtonClick = async (
    contentId: number,
    form: FormInstance,
    uploadVideo?: UploadVideoInterface,
    onProgress?: (videoFile: UploadVideoInterface) => void
  ): Promise<void> => {
    const loadingMessage = message.loading('Subtitle upload...', 0);
    const subtitleList = form.getFieldValue('subtitleList');
    if (!subtitleList) {
      Modal.error({
        title: 'Error',
        content: 'No subtitle list found',
        okText: 'Confirm',
      });
      loadingMessage();
    } else {
      const uploadSubtitleList = subtitleList.filter(
        (subtitle: SubtitleInterface) => subtitle.id < 0
      );
      const filteredSubtitleList = uploadSubtitleList.filter(
        (subtitle: SubtitleInterface) => subtitle.lang !== '' && subtitle.file !== undefined
      );

      if (filteredSubtitleList.length > 0) {
        try {
          await contentService.uploadSubtitle(contentId, filteredSubtitleList);
        } catch (error) {
          console.log(error);
        }
        message.success('Subtitle upload success');
      }

      const updateSubtitleList = subtitleList.filter(
        (subtitle: SubtitleInterface) => subtitle.id > 0
      );

      if (updateSubtitleList.length > 0) {
        try {
          await contentService.updateSubtitle(contentId, updateSubtitleList);
        } catch (error) {
          console.log(error);
        }
        message.success('Subtitle update success');
        form.resetFields(['subtitleList']);
      }

      loadingMessage();
    }

    if (uploadVideo) {
      const loadingMessage = message.loading('Video upload...', 0);
      const uploadResult = await originVideoUploadService.uploadVideoFiles(
        [uploadVideo].map(file => {
          return {
            ...file,
            title: file.name,
            state: 'normal',
          };
        }) as UploadVideoInterface[],
        false,
        contentId,
        onProgress
      );

      if (uploadResult) {
        loadingMessage();
        // window.location.reload();
      }
      loadingMessage();
    }
  };

  return {
    handleRefresh,
    handleStartTranscode,
    handleRetranscodeWithEmbeddedSubtitle,
    handleUpdateVideoManifest,
    handleSaveButtonClick,
  };
};

export default transcodeHandlers;
